'use client'
import PlaceBidLabel from './place-bid-label'
import OfferLinkArrow from './offer-link-arrow'
import { SHOW_OFFER_RECOVERY } from '@/utils/offer-ui-flags'
import { useEffect, useState } from 'react'
import useSWR from 'swr'
import Link from 'next/link'
import { RefreshCw, Zap } from 'lucide-react'
import { OFFERS_API_URL } from '@/utils/constants'
import { NAT_COLLECTION, formatNatAmount, natAmount } from '@/utils/nat-offers'
import { offersEnabledFor } from '@/utils/offers'
import { useCollectionOffers } from '@/utils/hooks/useOffers'
import type { Offer } from '@/utils/offers'
import { activeCollectionOffers } from '@/utils/offer-book'
import MakeOfferModal from './make-offer-modal'
import CancelOfferModal from './cancel-offer-modal'
import NatInstantSell from './nat-instant-sell'
import Wallet from '@/utils/wallet'
import useLoggedIn from '@/utils/hooks/useLoggedIn'
import styles from './trade-panel.module.css'

export default function NatOffersPanel() {
	const [mounted, setMounted] = useState(false)
	useEffect(() => setMounted(true), [])
	const [creating, setCreating] = useState(false)
	const [selling, setSelling] = useState<string | null>(null)
	const [cancel, setCancel] = useState<Offer | null>(null)
	const [mine, setMine] = useState(false)
	const [now, setNow] = useState(Date.now)
	useEffect(() => {
		const timer = setInterval(() => setNow(Date.now()), 1000)
		return () => clearInterval(timer)
	}, [])
	const loggedIn = useLoggedIn()
	const address = loggedIn ? Wallet.getTaprootAddress() : ''
	const { data: capability, error: capabilityError } = useSWR(
		'nat-offers:status',
		async () => {
			const res = await fetch(`${OFFERS_API_URL}/market/offers/nat/status?t=${Date.now()}`, {
				cache: 'no-store',
				signal: AbortSignal.timeout(10000)
			})
			if (!res.ok) throw new Error('NAT status unavailable')
			return res.json() as Promise<{ enabled: boolean }>
		},
		{ refreshInterval: 30000, shouldRetryOnError: false }
	)
	const { data, error, isLoading, isValidating, mutate } = useCollectionOffers(NAT_COLLECTION)
	const offers = activeCollectionOffers(data, now)
		.filter((o) => o.nat_terms && natAmount(o.nat_terms.token_amount))
		.sort((a, b) => {
			const left = BigInt(a.price_sats) * natAmount(b.nat_terms!.token_amount)!
			const right = BigInt(b.price_sats) * natAmount(a.nat_terms!.token_amount)!
			return left > right ? -1 : left < right ? 1 : 0
		})
	const visible = offers.filter((o) => !mine || o.buyer_address === address)
	const enabled = capability?.enabled === true && !capabilityError
	// DMT-NAT lots are TAP tokens: the panel stays hidden until fungible
	// offers roll out, whatever the isolated API reports.
	const natOffersOpen = mounted && offersEnabledFor(NAT_COLLECTION)
	if (!mounted)
		return (
			<section className={styles.panel} aria-label="NAT collection offers">
				<p role="status">Loading offers…</p>
			</section>
		)
	if (!natOffersOpen) return null
	return (
		<section className={styles.panel} aria-label="NAT collection offers">
			<header className={styles.heading}>
				<h2>DMT-NAT order book</h2>
				<button aria-label="Refresh NAT offers" disabled={isValidating} onClick={() => void mutate()}>
					<RefreshCw size={14} />
				</button>
			</header>
			<div className={styles.heading}>
				<span className={styles.note}>Top offer</span>
				<strong>{offers[0] ? `${offers[0].nat_terms!.sats_per_nat} sats/NAT` : '—'}</strong>
			</div>
			<div className={styles.actions}>
				<button
					className={styles.secondary}
					disabled={
						!enabled ||
						!offers.some((o) => o.buyer_address !== address && o.buyer_payment_address !== address)
					}
					onClick={() => setSelling('')}
				>
					<Zap size={13} className="mr-1 inline" />
					Instant sell
				</button>
				<button className={styles.primary} onClick={() => setCreating(true)}>
					<PlaceBidLabel />
				</button>
			</div>
			{!enabled && (capability || capabilityError) && (
				<p className={styles.note} role="status">
					NAT funding is temporarily unavailable.
				</p>
			)}
			<div className={styles.tabs}>
				<button aria-pressed={!mine} onClick={() => setMine(false)}>
					All offers {offers.length}
				</button>
				<button aria-pressed={mine} onClick={() => setMine(true)}>
					My offers
				</button>
			</div>
			{error ? (
				<p className={styles.empty} role="alert">
					Could not refresh offers.{' '}
					<button className={styles.textButton} onClick={() => void mutate()}>
						Retry
					</button>
				</p>
			) : isLoading ? (
				<p className={styles.empty} role="status">
					Loading offers…
				</p>
			) : !visible.length ? (
				<p className={styles.empty}>
					{mine ? 'No active offers from this wallet.' : 'No active offers. Set your price to start.'}
				</p>
			) : (
				<table className={styles.table}>
					<thead>
						<tr>
							<th>Price (sats/NAT)</th>
							<th>NAT amount</th>
							<th>Total (BTC)</th>
						</tr>
					</thead>
					<tbody>
						{visible.map((o) => (
							<tr key={o.id}>
								<td>
									<button
										disabled={!enabled && o.buyer_address !== address}
										onClick={() => (o.buyer_address === address ? setCancel(o) : setSelling(o.id))}
									>
										{o.nat_terms!.sats_per_nat}
									</button>
									<small>{o.buyer_address === address ? 'Your offer' : 'Funded offer'}</small>
								</td>
								<td>
									{formatNatAmount(o.nat_terms!.token_amount)}
									<small>Exact lot</small>
								</td>
								<td>
									{(o.price_sats / 1e8).toFixed(8)}
									<small>
										<time dateTime={o.expires_at} title={new Date(o.expires_at).toLocaleString()}>
											{Math.max(1, Math.ceil((Date.parse(o.expires_at) - now) / 3600000))}h left
										</time>
									</small>
									{o.buyer_address === address && (
										<button onClick={() => setCancel(o)}>Cancel & refund</button>
									)}
								</td>
							</tr>
						))}
					</tbody>
				</table>
			)}
			<div className={styles.manage}>
				<Link href="/wallet#offers">
					Manage offers <OfferLinkArrow />
				</Link>
				{SHOW_OFFER_RECOVERY && (
					<details>
						<summary>Advanced</summary>
						<Link href="/offers/recovery">
							Emergency recovery <OfferLinkArrow />
						</Link>
					</details>
				)}
			</div>
			{creating && (
				<MakeOfferModal
					open
					onClose={() => setCreating(false)}
					collectionSlug={NAT_COLLECTION}
					name="DMT-NAT"
					initialScope="collection"
					natEnabled={enabled}
					onPlaced={() => void mutate()}
				/>
			)}
			{selling !== null && (
				<NatInstantSell
					offers={error ? [] : offers}
					initialOffer={selling}
					onClose={() => setSelling(null)}
					onChanged={() => void mutate()}
				/>
			)}
			{cancel && (
				<CancelOfferModal
					offer={cancel}
					onClose={() => setCancel(null)}
					onChanged={() => void mutate()}
				/>
			)}
		</section>
	)
}
